/**
 * The Dispatch log's "All people" table (plan 2026-10-15-004 §6.2, §6.4): one
 * row per person per send — tests and real, one-person and whole-roster —
 * newest first, read a page at a time from `GET /v2/plays/:id/log`.
 *
 *   columns  When · Person (+ group) · Type Test/Real · Channel · Status, then
 *            the same expand-to-detail a send card's row has (`SendLogRow`,
 *            reused with its optional extra cells);
 *   filters  Type (All / Tests / Real) and Status chips whose numbers are
 *            summed from the per-send summaries the Dispatch log already
 *            loaded — no extra read — and "Only this person", a removable chip
 *            held in `dispatch-log-filter-store` so the member panel's "All of
 *            Ana's sends" can set it;
 *   a name   opens that person's side panel on THIS row (`onOpenPerson`).
 *
 * Every rep-facing word comes from `dispatch-rep-status.ts` (the ONE
 * classifier); no id or status code is ever rendered. Type and Status are
 * plain view state; a Status whose bucket has emptied falls back to All at
 * render, never in an effect.
 */
import { useState } from 'react';
import { X } from '@phosphor-icons/react';
import { Button } from '@clay-run/glaze/button';
import { Chip } from '@clay-run/glaze/chip';
import { Icon } from '@clay-run/glaze/icon';
import { LoadingSpinner } from '@clay-run/glaze/loading-spinner';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@clay-run/glaze/table';
import { Text } from '@clay-run/glaze/text';

import type { CampaignMember } from '../../../../_shared/launcher-campaign.types';
import {
  REP_STATUS_CHIP_LABEL,
  REP_STATUS_ORDER,
  classifyRepStatus,
  type RepDispatchStatus,
} from '../../../dispatch-rep-status';
import { clearPersonFilter, showOnlyPerson, useDispatchLogFilterStore } from '../../../dispatch-log-filter-store';
import type { MemberResultFocus } from '../../../member-test-store';
import type { PlaybooksDispatchLogRow, PlaybooksPlaySendsResponse, PlaybooksSendSummary } from '../../../schemas';

import { formatSentAt } from './SendLogCard';
import { SendLogRow } from './SendLogRow';
import { usePlayLogRows } from './usePlayLogRows';
import { useSendRecheck } from './useSendRecheck';
import { useSendRetry } from './useSendRetry';

type Kind = 'all' | 'test' | 'real';
type StatusFilter = RepDispatchStatus | 'all';

const KIND_LABEL: Record<Kind, string> = { all: 'All', test: 'Tests', real: 'Real' };
const COLUMNS = 6;

export interface PlayLogTableProps {
  playId: string;
  /** The sends query's data — the source of the chip counts and of `live`. */
  sends: PlaybooksPlaySendsResponse | undefined;
  /** Any send is still going: keep the loaded pages fresh. */
  live: boolean;
  /** The campaign roster: only people in it get a name that opens a panel. */
  members: readonly CampaignMember[];
  /** Opens a person's panel focused on the exact row; absent = names are plain text. */
  onOpenPerson?: (memberId: string, focus: MemberResultFocus) => void;
}

function sumStatuses(summaries: readonly PlaybooksSendSummary[]): Record<RepDispatchStatus, number> {
  const totals = Object.fromEntries(REP_STATUS_ORDER.map((status) => [status, 0])) as Record<RepDispatchStatus, number>;
  for (const summary of summaries) for (const status of REP_STATUS_ORDER) totals[status] += summary.byStatus[status] ?? 0;
  return totals;
}

export function PlayLogTable({ playId, sends, live, members, onOpenPerson }: PlayLogTableProps) {
  const [kind, setKind] = useState<Kind>('all');
  const [status, setStatus] = useState<StatusFilter>('all');
  const personMemberId = useDispatchLogFilterStore((state) => state.personMemberId);
  const personLabel = useDispatchLogFilterStore((state) => state.personLabel);

  const realSummaries = (sends?.sends ?? []).map((send) => send.summary);
  const testSummaries = (sends?.testSends ?? []).map((send) => send.summary);
  const realTotal = realSummaries.reduce((sum, s) => sum + s.total, 0);
  const testTotal = testSummaries.reduce((sum, s) => sum + s.total, 0);
  const counted = kind === 'all' ? [...realSummaries, ...testSummaries] : kind === 'test' ? testSummaries : realSummaries;
  const statusCounts = sumStatuses(counted);
  // Per-send summaries cover everyone, so with a person filter the numbers would overstate: show chips without them.
  const showCounts = personMemberId === null;
  const presentStatuses = REP_STATUS_ORDER.filter((s) => !showCounts || statusCounts[s] > 0);
  const activeStatus: StatusFilter = status !== 'all' && showCounts && statusCounts[status] === 0 ? 'all' : status;
  const activeTotal =
    activeStatus === 'all' ? counted.reduce((sum, s) => sum + s.total, 0) : statusCounts[activeStatus];

  const log = usePlayLogRows({
    playId,
    source: {
      kind: 'play',
      filters: { kind, status: activeStatus, ...(personMemberId === null ? {} : { memberId: personMemberId }) },
    },
    enabled: true,
    live,
  });

  const nowMs = Date.now();
  const rosterIds = new Set(members.map((m) => m.id));
  const entries = log.rows.map((row) => ({ row, view: classifyRepStatus(row, nowMs) }));

  return (
    <div className="flex flex-col" data-play-log-table="true">
      <div className="flex flex-col gap-1.5 border-b border-border-secondary px-3 py-2">
        <div className="flex flex-wrap items-center gap-1.5" data-play-log-type-chips="true">
          {(['all', 'test', 'real'] as const).map((value) => (
            <FilterChip
              key={value}
              label={KIND_LABEL[value]}
              count={showCounts ? (value === 'all' ? realTotal + testTotal : value === 'test' ? testTotal : realTotal) : null}
              active={kind === value}
              onClick={() => setKind(value)}
            />
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-1.5" data-play-log-status-chips="true">
          <FilterChip
            label="All"
            count={showCounts ? counted.reduce((sum, s) => sum + s.total, 0) : null}
            active={activeStatus === 'all'}
            onClick={() => setStatus('all')}
          />
          {presentStatuses.map((value) => (
            <FilterChip
              key={value}
              label={REP_STATUS_CHIP_LABEL[value]}
              count={showCounts ? statusCounts[value] : null}
              active={activeStatus === value}
              onClick={() => setStatus(value)}
            />
          ))}
        </div>
        {personMemberId !== null && (
          <div className="flex items-center gap-1.5" data-play-log-person-chip="true">
            <Text size="xs" variant="tertiary">
              Only
            </Text>
            <Chip size="sm" color="blue">
              {personLabel ?? 'This person'}
              <button
                type="button"
                aria-label={`Show everyone's sends again (remove ${personLabel ?? 'this person'})`}
                className="ml-1 inline-flex cursor-pointer items-center"
                onClick={clearPersonFilter}
              >
                <Icon type={X} size="sm" />
              </button>
            </Chip>
          </div>
        )}
      </div>

      <Table dense bleed>
        <TableHead>
          <TableRow>
            <TableHeader width={28}>
              <span className="sr-only">Expand</span>
            </TableHeader>
            <TableHeader>When</TableHeader>
            <TableHeader>Person</TableHeader>
            <TableHeader>Type</TableHeader>
            <TableHeader>Channel</TableHeader>
            <TableHeader>Status</TableHeader>
          </TableRow>
        </TableHead>
        <TableBody>
          {log.isLoading && (
            <TableRow>
              <TableCell colSpan={COLUMNS}>
                <div className="flex justify-center py-4">
                  <LoadingSpinner size="sm" />
                </div>
              </TableCell>
            </TableRow>
          )}
          {!log.isLoading && !log.isError && entries.length === 0 && !log.hasNextPage && (
            <TableRow>
              <TableCell colSpan={COLUMNS}>
                <Text size="xs" variant="tertiary" className="py-4 text-center">
                  {kind === 'all' && activeStatus === 'all' && personMemberId === null
                    ? 'No sends yet.'
                    : 'No sends match these filters.'}
                </Text>
              </TableCell>
            </TableRow>
          )}
          {entries.map(({ row, view }) => (
            <PlayLogRow
              key={row.id}
              playId={playId}
              row={row}
              view={view}
              onOpenPerson={
                onOpenPerson !== undefined && row.campaign_member_id !== null && rosterIds.has(row.campaign_member_id)
                  ? () =>
                      onOpenPerson(row.campaign_member_id!, {
                        dispatchId: row.dispatch_id,
                        logId: row.id,
                        isTest: row.is_test === true,
                      })
                  : undefined
              }
            />
          ))}
        </TableBody>
      </Table>

      {log.isError && (
        <div className="flex items-center justify-between gap-2 border-t border-border-secondary px-3 py-2">
          <Text size="xs" variant="critical">
            Couldn&apos;t load the sends.
          </Text>
          <Button variant="secondary" size="xs" onClick={log.retryLoad}>
            Try again
          </Button>
        </div>
      )}
      {entries.length > 0 && (
        <div className="flex items-center justify-between gap-2 border-t border-border-secondary px-3 py-2">
          <Text size="xs" variant="tertiary">
            {showCounts
              ? `Showing ${entries.length.toLocaleString('en-US')} of ${activeTotal.toLocaleString('en-US')}`
              : `Showing ${entries.length.toLocaleString('en-US')}`}
          </Text>
          {log.hasNextPage && (
            <Button variant="secondary" size="xs" disabled={log.isFetchingNextPage} onClick={log.loadMore}>
              {log.isFetchingNextPage && <LoadingSpinner size="sm" />}
              Load more
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * One row, with the Retry / Check again hooks scoped to ITS dispatch — a page
 * mixes dispatches, and the hooks are per dispatch. `0` stale rows: the
 * automatic re-check is the send card's (it fires when the card mounts); from
 * this table only "Check again" asks Clay.
 */
function PlayLogRow({
  playId,
  row,
  view,
  onOpenPerson,
}: {
  playId: string;
  row: PlaybooksDispatchLogRow;
  view: ReturnType<typeof classifyRepStatus>;
  onOpenPerson: (() => void) | undefined;
}) {
  const retry = useSendRetry(playId, row.dispatch_id, [row]);
  const recheck = useSendRecheck(playId, row.dispatch_id, 0);
  const displayName = row.contact_name ?? row.contact_email ?? 'Unnamed contact';
  return (
    <SendLogRow
      playId={playId}
      row={row}
      view={view}
      retry={retry}
      recheck={recheck}
      detailColSpan={COLUMNS}
      onOpenPerson={onOpenPerson}
      leadingCells={
        <TableCell>
          <Text size="xs" variant="tertiary">
            {row.dispatched_at === null ? '—' : formatSentAt(row.dispatched_at)}
          </Text>
        </TableCell>
      }
      afterPersonCells={
        <TableCell>
          <Chip size="xs" color={row.is_test === true ? 'default' : 'green'}>
            {row.is_test === true ? 'Test' : 'Real'}
          </Chip>
        </TableCell>
      }
      personActions={
        row.campaign_member_id !== null ? (
          <Button
            variant="ghost"
            size="xs"
            aria-label={`Only show ${displayName}`}
            onClick={(event) => {
              event.stopPropagation();
              showOnlyPerson(row.campaign_member_id!, displayName);
            }}
          >
            Only this person
          </Button>
        ) : undefined
      }
    />
  );
}

function FilterChip({
  label,
  count,
  active,
  onClick,
}: {
  label: string;
  count: number | null;
  active: boolean;
  onClick: () => void;
}) {
  // Same blue-when-active Terra override the send card's chips use.
  return (
    <Chip size="sm" color={active ? 'blue' : 'default'} onClick={onClick}>
      {label}
      {count !== null && <span className="ml-1 text-content-tertiary">{String(count)}</span>}
    </Chip>
  );
}
